import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { auth } from '../firebase';
import { API_BASE_URL } from '../config/api';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signup: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Renamed for clarity: performs the actual sync POST request
  const performSyncWithBackend = useCallback(async (user: User, token: string) => {
    console.log("performSyncWithBackend: Attempting to sync for user:", user.uid);
    try {
      console.log("performSyncWithBackend: Calling fetch to /api/users/sync"); // Log before fetch
      const response = await fetch(`${API_BASE_URL}/api/users/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to parse error response' }));
        const errorMessage = `Sync failed: ${response.status} - ${errorData.error || errorData.message || 'Unknown error'}`;
        console.error(errorMessage, errorData);
        setSyncError(errorMessage);
        if (response.status === 409) {
          alert(`Could not create your profile: The username '${user.displayName || user.email?.split('@')[0]}' might already be taken.`);
        }
      } else {
        const syncResult = await response.json();
        console.log("Sync successful:", syncResult);
      }
    } catch (error: any) {
      console.error("performSyncWithBackend: Error during fetch POST request:", error);
      // Log the error object itself for more details
      console.error("performSyncWithBackend: Error details:", error);
      setSyncError(`Sync POST fetch failed: ${error.message}`);
    }
  }, []);

  // New function to check profile existence before syncing
  const checkAndSyncUser = useCallback(async (user: User) => {
      if (!user) return;
      setSyncError(null);
      console.log("checkAndSyncUser: Checking profile existence for user:", user.uid);

      try {
          console.log("checkAndSyncUser: Getting ID token..."); // Log token step
          const token = await user.getIdToken();
          console.log("checkAndSyncUser: Got ID token. Fetching profile..."); // Log profile fetch step
          // We still fetch the profile first to see if it exists, but we will sync regardless
          const profileResponse = await fetch(`${API_BASE_URL}/api/users/profile`, {
              method: 'GET',
              headers: {
                  'Authorization': `Bearer ${token}`,
              },
          });
          console.log(`checkAndSyncUser: Profile fetch response status: ${profileResponse.status}`); // Log profile status

          if (profileResponse.ok) {
              // Profile exists, log it but proceed to sync anyway to ensure data is fresh
              console.log("checkAndSyncUser: Profile exists, proceeding with sync to update...");
          } else if (profileResponse.status === 404) {
              // Profile not found, definitely need to sync
              console.log("checkAndSyncUser: Profile not found, initiating sync...");
          } else {
              // Handle other errors during profile check, but still attempt sync
              const errorData = await profileResponse.json().catch(() => ({ message: 'Failed to parse error response' }));
              const errorMessage = `Profile check failed: ${profileResponse.status} - ${errorData.error || errorData.message || 'Unknown error'}. Attempting sync anyway.`;
              console.error("checkAndSyncUser:", errorMessage, errorData);
              setSyncError(errorMessage); // Keep error message for info, but don't block sync
          }

          // Always call performSyncWithBackend regardless of profile check outcome
          console.log("checkAndSyncUser: Preparing to call performSyncWithBackend."); // Log before sync call
          await performSyncWithBackend(user, token);
          console.log("checkAndSyncUser: Call to performSyncWithBackend finished."); // Log after sync call

      } catch (error: any) {
          // Catch errors specifically from the getIdToken or fetch calls within this block
          console.error("checkAndSyncUser: Error during pre-sync check (token/profile fetch):", error);
          setSyncError(`Pre-sync check failed: ${error.message}`);
          // Optionally, you might still try to sync even if the profile check failed,
          // but it's safer to stop here if we couldn't even get the token.
          // await performSyncWithBackend(user, token); // Decide if sync should proceed on error
      }
  }, [performSyncWithBackend]); // Dependency on the function that actually syncs

  function signup(email: string, password: string) {
    return createUserWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        setCurrentUser(userCredential.user);
        await checkAndSyncUser(userCredential.user);
      });
  }

  function login(email: string, password: string) {
    return signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        setCurrentUser(userCredential.user);
        await checkAndSyncUser(userCredential.user);
      });
  }

  function logout() {
    return signOut(auth)
      .then(() => {
        setCurrentUser(null);
      });
  }

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider)
      .then(async (result) => {
        setCurrentUser(result.user);
        await checkAndSyncUser(result.user);
      });
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // User is signed in, BUT DO NOT SYNC HERE ANYMORE
      } else {
        // User is signed out
        setSyncError(null); // Clear sync error on logout
      }
      setLoading(false); // Set loading false once auth state is determined
    });

    return unsubscribe;
  }, []); // Dependency array is now empty as checkAndSyncUser is called elsewhere

  const value = {
    currentUser,
    loading,
    signup,
    login,
    logout,
    signInWithGoogle
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 